import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { MatLegacyPaginator as MatPaginator } from '@angular/material/legacy-paginator';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { MatSort } from '@angular/material/sort';
import { DisplayMode } from 'cbc-shared-lib';
import { Subject } from 'rxjs';
import { Sgke162aResponseSearchresultList } from 'sg-api';

@Component({
  selector: 'app-sgke160d-search-result',
  templateUrl: './sgke160d-search-result.component.html',
  styleUrl: './sgke160d-search-result.component.scss'
})
export class Sgke160dSearchResultComponent implements OnInit, OnDestroy {
  /** モード */
  @Input() mode: string;
  /** 検索結果リスト */
  @Input() searchresultList: Array<Sgke162aResponseSearchresultList>;

  /** 締め取消ボタン押下Emitter */
  @Output() onClickCancelEmitter = new EventEmitter();
  /** 入金締めボタン押下Emitter */
  @Output() onClickRegisterEmitter = new EventEmitter();
  /** 承認ボタン押下Emitter */
  @Output() onClickApprovalEmitter = new EventEmitter();

  /** ページング */
  @ViewChild('paginator1', { static: true }) paginator: MatPaginator;
  /** ソート */
  @ViewChild('sort1', { static: true }) sort: MatSort;

  /** 入金締め取得元区分コード_入金 */
  private static RECEIVEMONEYJIME_SHTKMT_DIVCD_RCVMNY = '0';
  /** 入金締め取得元区分コード_返金 */
  private static RECEIVEMONEYJIME_SHTKMT_DIVCD_REPAY = '1';

  /** 入金締めステータス_入金締め未済 */
  private static RECEIVEMONEYJIME_STATUSCODE_UNCOMPLETED = '1';
  /** 入金締めステータス_入金締め承認依頼 */
  private static RECEIVEMONEYJIME_STATUSCODE_APPROVAL_REQUEST = '2';
  /** 入金締めステータス_入金締め済 */
  private static RECEIVEMONEYJIME_STATUSCODE_COMPLETED = '3';

  /** 購読を停止するためのSubject */
  private onDestroy$ = new Subject();

  /** モード区分_登録 */
  public MODE_CREATE = DisplayMode.create;
  /** モード区分_承認 */
  public MODE_APPROVE = DisplayMode.approve;

  /** データソース */
  dataSource: MatTableDataSource<Sgke162aResponseSearchresultList>;
  /** フッターデータソース */
  footerDataSource: MatTableDataSource<{
    kubun: string;
    kensu: number;
    mny: number;
  }>;

  /** テーブル表示カラム */
  displayedColumnsCol: string[] = [
    'payCtrNo',
    'visitName',
    'rcvmnyKeitai.aname',
    'rcvmnyRepay.aname',
    'claimMny',
    'inmny',
    'trnfFee',
    'rcvmnyYearmonthdate',
  ];
  /** フッターテーブル表示カラム */
  displayedFooterColumnsCol: string[] = [
    'kubun',
    'kensu',
    'mny',
  ];

  /**
   * コンストラクタ
   * @memberof Sgke160dSearchResultComponent
   */
  constructor(
  ) {
  }

  /**
   * 入金締めステータスが入金締め未済の場合、trueを返却
   * @returns {boolean}
   * @memberof Sgke160dSearchResultComponent
   */
  get isUncompletedStat(): boolean {
    return this.dataSource.data.every(
      currentValue =>
        currentValue.receivemoneyjimeStatuscode == Sgke160dSearchResultComponent.RECEIVEMONEYJIME_STATUSCODE_UNCOMPLETED
    )
  };

  /**
   * 入金締めステータスが入金締め承認依頼の場合、trueを返却
   * @returns {boolean}
   * @memberof Sgke160dSearchResultComponent
   */
  get isApprovalRequestStat(): boolean {
    return this.dataSource.data.every(
      currentValue =>
        currentValue.receivemoneyjimeStatuscode == Sgke160dSearchResultComponent.RECEIVEMONEYJIME_STATUSCODE_APPROVAL_REQUEST
    )
  };

  /**
   * 初期処理
   * @returns {void}
   * @memberof Sgke160dSearchResultComponent
   */
  ngOnInit(): void {
    // 表データと、ソート/ページングを連携
    this.dataSource = new MatTableDataSource<Sgke162aResponseSearchresultList>(this.searchresultList);
    this.footerDataSource = new MatTableDataSource([
      {
        kubun: '入金',
        kensu: this.getTotalCount(Sgke160dSearchResultComponent.RECEIVEMONEYJIME_SHTKMT_DIVCD_RCVMNY),
        mny: this.getSum('inmny', Sgke160dSearchResultComponent.RECEIVEMONEYJIME_SHTKMT_DIVCD_RCVMNY),
      },
      {
        kubun: '返金',
        kensu: this.getTotalCount(Sgke160dSearchResultComponent.RECEIVEMONEYJIME_SHTKMT_DIVCD_REPAY),
        mny: this.getSum('inmny', Sgke160dSearchResultComponent.RECEIVEMONEYJIME_SHTKMT_DIVCD_REPAY),
      },
    ]);

    this.dataSource.sortingDataAccessor = (item, property) => {
      return property
        .split('.')
        .reduce((acc, key) => acc?.[key], item);
    };

  }

  /**
   * ViewInit後の呼び出し
   * @returns {void}
   * @memberof Sgke160dSearchResultComponent
   */
  ngAfterViewInit(): void {
    this.dataSource.paginator ??= this.paginator;
    this.dataSource.sort ??= this.sort;
  }

  /**
   * 購読解除
   * @returns {void}
   * @memberof Sgke160dSearchResultComponent
   */
  ngOnDestroy(): void {
    //購読を停止する処理
    this.onDestroy$.next();
  }

  /**
   * 指定したkeyの合計値を算出
   * @param {string} key 
   * @param {string} kubun 
   * @returns {number}
   * @memberof Sgke160dSearchResultComponent
   */
  getSum(key: string, kubun?: string): number {
    const list = this.searchresultList?.filter(row => kubun ? row.rcvmnyRepay?.codeValue == kubun : true);
    return list?.reduce((p, c) => p + c[key], 0);
  }

  /**
   * 指定した入金締め取得元区分に紐づくデータをカウント
   * @param {string} kubun 
   * @returns {number}
   * @memberof Sgke160dSearchResultComponent
   */
  getTotalCount(kubun: string): number {
    const list = this.searchresultList?.filter(row => kubun ? row.rcvmnyRepay?.codeValue == kubun : true);
    return list?.length;
  }

  /**
   * 締め取消ボタン押下時処理
   * @returns {void}
   * @memberof Sgke160dSearchResultComponent
   */
  onClickCancel(): void {
    // 親コンポーネントに通知
    this.onClickCancelEmitter.emit();
  }

  /**
   * 入金締めボタン押下時処理
   * @returns {void}
   * @memberof Sgke160dSearchResultComponent
   */
  onClickRegist(): void {
    // 親コンポーネントに通知
    this.onClickRegisterEmitter.emit();
  }

  /**
   * 承認ボタン押下時処理
   * @returns {void}
   * @memberof Sgke160dSearchResultComponent
   */
  onClickApproval(): void {
    // 親コンポーネントに通知
    this.onClickApprovalEmitter.emit();
  }

}
